"use client"

import React, { useState, useEffect, useCallback } from "react"
import { FaFileExcel, FaSpinner, FaShieldAlt, FaChevronDown, FaChevronRight } from "react-icons/fa"
import { FiRefreshCw, FiCheck, FiX } from "react-icons/fi"
import api from "../../libs/axios"
import * as XLSX from "xlsx"
import toast from "react-hot-toast"
import { useAuth } from "../../provider/auth.provider"

interface Permission {
  permission_id: number
  name: string
  description?: string
  resource: string
  action?: string
}

interface Role {
  id: number
  name: string
  level: number
  role_permissions?: Array<{ permission_id: number }>
}

const PermissionReportPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const [roles, setRoles] = useState<Role[]>([])
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [permissionSet, setPermissionSet] = useState<Map<string, Set<number>>>(new Map())
  const [collapsedResources, setCollapsedResources] = useState<Set<string>>(new Set())

  const canView = hasPermission("reports", "view_permission_report")

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [rolesRes, permRes] = await Promise.all([
        api.get("/roles/list", { params: { page: 1, limit: 500, include_permissions: "true" } }),
        api.get("/roles/permissions/all"),
      ])

      const roleList: Role[] = rolesRes.data?.data?.roles || rolesRes.data?.data || []
      const permList: Permission[] = permRes.data?.data || []

      const map = new Map<string, Set<number>>()
      for (const role of roleList) {
        const granted = new Set<number>(
          (role.role_permissions || []).map((rp) => rp.permission_id)
        )
        map.set(String(role.id), granted)
      }

      setRoles(roleList.sort((a, b) => a.level - b.level))
      setPermissions(permList)
      setPermissionSet(map)
    } catch (err: any) {
      toast.error("Failed to load permission data")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (canView) fetchData()
  }, [canView, fetchData])

  const hasGrant = (roleId: number, permId: number) =>
    permissionSet.get(String(roleId))?.has(permId) ?? false

  const toggleResource = (resource: string) => {
    setCollapsedResources(prev => {
      const next = new Set(prev)
      if (next.has(resource)) next.delete(resource)
      else next.add(resource)
      return next
    })
  }

  const grouped = permissions.reduce<Record<string, Permission[]>>((acc, perm) => {
    const res = perm.resource || "Other"
    if (!acc[res]) acc[res] = []
    acc[res].push(perm)
    return acc
  }, {})

  const resourceKeys = Object.keys(grouped)

  const collapseAll = () => setCollapsedResources(new Set(resourceKeys))
  const expandAll = () => setCollapsedResources(new Set())

  const handleExportExcel = () => {
    if (exporting) return
    setExporting(true)
    try {
      const headers = ["Resource", "Permission Name", ...roles.map((r) => r.name)]
      const rows = permissions.map((perm) => [
        perm.resource,
        perm.name,
        ...roles.map((role) => (hasGrant(role.id, perm.permission_id) ? "Yes" : "No")),
      ])

      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
      const colWidths = headers.map((h, i) => ({
        wch: i < 2 ? 30 : Math.max(h.length + 2, 8),
      }))
      ws["!cols"] = colWidths

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, "Permission Report")
      XLSX.writeFile(wb, "permission_report.xlsx")
      toast.success("Excel exported successfully")
    } catch {
      toast.error("Failed to export Excel")
    } finally {
      setExporting(false)
    }
  }

  if (!canView) {
    return (
      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <FaShieldAlt className="w-5 h-5 text-blue-800" />
            Permission Report
          </h2>
        </div>
        <div className="flex flex-col items-center justify-center h-64 text-gray-400">
          <FaShieldAlt className="w-12 h-12 mb-3 text-gray-300" />
          <p className="text-lg font-medium">Access Denied</p>
          <p className="text-sm mt-1">You do not have permission to view this report.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      {/* Header — sticky so the title + export/refresh controls stay reachable while scrolling */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white sticky top-0 z-40">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <FaShieldAlt className="w-5 h-5 text-blue-800" />
            Permission Report
          </h2>
          <div className="flex gap-2 flex-wrap items-center">
            <button
              onClick={expandAll}
              disabled={loading}
              className="px-3 py-2 text-xs font-medium rounded border border-gray-300 hover:bg-gray-50 text-gray-600 disabled:opacity-50"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              disabled={loading}
              className="px-3 py-2 text-xs font-medium rounded border border-gray-300 hover:bg-gray-50 text-gray-600 disabled:opacity-50"
            >
              Collapse All
            </button>
            <button
              onClick={fetchData}
              disabled={loading}
              className="p-2 rounded border border-gray-300 hover:bg-gray-50 text-gray-600 disabled:opacity-50"
              title="Refresh"
            >
              <FiRefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={handleExportExcel}
              disabled={exporting || loading || permissions.length === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {exporting ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaFileExcel className="w-4 h-4" />}
              Export Excel
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-1">Role × Permission matrix — Yes/No for each role</p>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex flex-col items-center justify-center h-48 text-gray-400">
          <FaSpinner className="w-8 h-8 animate-spin mb-2" />
          <p className="text-sm">Loading permission data...</p>
        </div>
      ) : permissions.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p>No permissions found.</p>
        </div>
      ) : (
        /* Bounded scroll container so the header row can stick vertically (and the
           first column can stick horizontally). Height leaves room for the app chrome. */
        <div className="overflow-auto max-h-[calc(100vh-200px)]">
          <table className="w-full text-xs border-collapse">
            <thead className="bg-gray-50 sticky top-0 z-30">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[220px] border-r border-b border-gray-200 sticky left-0 top-0 z-40 bg-gray-50">
                  Permission
                </th>
                {roles.map((role) => (
                  <th
                    key={role.id}
                    className="px-3 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[90px] border-r border-b border-gray-200 last:border-r-0 whitespace-nowrap bg-gray-50"
                  >
                    {role.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {resourceKeys.map((resource) => {
                const perms = grouped[resource]
                const isCollapsed = collapsedResources.has(resource)
                return (
                  <React.Fragment key={resource}>
                    {/* Resource header row */}
                    <tr
                      className="bg-blue-800 text-white cursor-pointer select-none hover:bg-blue-900 transition-colors"
                      onClick={() => toggleResource(resource)}
                    >
                      <td
                        className="px-4 py-2.5 font-semibold text-sm sticky left-0 z-20 bg-blue-800"
                        colSpan={roles.length + 1}
                      >
                        <div className="flex items-center gap-2">
                          {isCollapsed
                            ? <FaChevronRight className="w-3 h-3 flex-shrink-0" />
                            : <FaChevronDown className="w-3 h-3 flex-shrink-0" />
                          }
                          {resource}
                          <span className="ml-1 text-xs font-normal text-blue-200">({perms.length})</span>
                        </div>
                      </td>
                    </tr>
                    {/* Permission rows */}
                    {!isCollapsed && perms.map((perm, idx) => (
                      <tr
                        key={perm.permission_id}
                        className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                      >
                        <td className={`px-4 py-2.5 text-gray-700 border-r border-gray-200 sticky left-0 z-10 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                          {perm.name}
                          {perm.description && (
                            <p className="text-gray-400 text-[10px] mt-0.5">{perm.description}</p>
                          )}
                        </td>
                        {roles.map((role) => {
                          const granted = hasGrant(role.id, perm.permission_id)
                          return (
                            <td
                              key={role.id}
                              className="px-2 py-2.5 text-center border-r border-gray-100 last:border-r-0"
                            >
                              {granted ? (
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 mx-auto">
                                  <FiCheck className="w-3 h-3 text-green-700 font-bold" />
                                </span>
                              ) : (
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-50 mx-auto">
                                  <FiX className="w-3 h-3 text-red-400" />
                                </span>
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default PermissionReportPage